/*
 * Werkbank – Endlos-Scrollen für Kategorie und Suche (23.10.2026)
 * Markup: snippets/pp-endlos.liquid (<pp-endless> in #ProductGridContainer, Raster mit [data-pp-endless-list]).
 * - IntersectionObserver auf <pp-endless>: kommt es in Sichtweite (+ Vorlauf), wird data-next per fetch
 *   geladen (mit section_id = nur die Section), die <li> der Antwort werden ans Raster angehängt.
 * - data-next ist paginate.next.url aus Liquid – Filter, Sortierung und Suchbegriff bleiben darin erhalten.
 * - Filtern/Sortieren (facets.js) ersetzt den Inhalt von #ProductGridContainer; das neue <pp-endless>
 *   startet als Custom Element von selbst, das alte bricht laufende Anfragen ab.
 * - Ohne JS bleibt <pp-endless> versteckt und die Seitennavigation sichtbar.
 */
(function () {
  if (window.customElements && customElements.get('pp-endless')) return;
  if (!window.customElements || !('IntersectionObserver' in window) || !window.fetch) return;

  function fill(tpl, shown, total) {
    return String(tpl || '').replace(/\[shown\]/g, shown).replace(/\[count\]/g, total);
  }

  class PpEndless extends HTMLElement {
    connectedCallback() {
      this.list = this.findList(document);
      this.next = this.dataset.next || '';
      this.total = parseInt(this.dataset.total, 10) || 0;
      this.offset = parseInt(this.dataset.offset, 10) || 0;
      this.busy = false;
      this.statusEl = this.querySelector('.pp-endless__status');
      this.moreBtn = this.querySelector('.pp-endless__more');
      if (!this.list) return;

      // Seitennavigation nur mit JS ausblenden
      const scope = this.closest('#ProductGridContainer') || this.parentElement;
      scope.querySelectorAll('.pagination-wrapper').forEach((el) => {
        el.hidden = true;
        el.style.display = 'none';
      });
      this.hidden = false;

      this.onMore = () => {
        this.fails = 0;
        this.load().then(() => {
          if (this.io && this.next && !this.classList.contains('is-error')) {
            this.io.unobserve(this);
            this.io.observe(this);
          }
        });
      };
      if (this.moreBtn) this.moreBtn.addEventListener('click', this.onMore);

      if (!this.next) {
        this.finish();
        return;
      }
      this.showLoading();
      this.io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) this.load();
      }, { rootMargin: '100000px 0px 900px 0px' });
      // Vorlauf auch nach oben: Ist die Fußzeile höher als der Bildschirm (Handy), liegt <pp-endless> am
      // Seitenende oberhalb des Bildschirms – es muss trotzdem nachladen („über/unter dem Ende = weiter“).
      this.io.observe(this);
      // Rückfall beim Scrollen (falls der Observer keine Änderung meldet)
      this.onScroll = () => {
        if (this.scrollT) return;
        this.scrollT = setTimeout(() => {
          this.scrollT = null;
          this.checkNear();
        }, 200);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    checkNear() {
      if (!this.next || this.busy || !this.isConnected) return;
      if (this.classList.contains('is-error') || this.classList.contains('is-done')) return;
      if (this.getBoundingClientRect().top < window.innerHeight + 900) this.load();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      clearTimeout(this.scrollT);
      if (this.ctrl) this.ctrl.abort();
      clearTimeout(this.retryT);
      if (this.moreBtn) this.moreBtn.removeEventListener('click', this.onMore);
    }

    findList(root) {
      return root.querySelector('[data-pp-endless-list]');
    }

    shown() {
      return Math.min(this.total || Infinity, this.offset + this.list.children.length);
    }

    setStatus(text) {
      if (this.statusEl) this.statusEl.textContent = text;
    }

    showLoading() {
      this.classList.remove('is-done', 'is-error');
      if (this.moreBtn) this.moreBtn.hidden = true;
      this.setStatus(this.dataset.loading + ' ' + fill(this.dataset.progress, this.shown(), this.total));
    }

    finish() {
      if (this.io) this.io.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      this.classList.add('is-done');
      if (this.moreBtn) this.moreBtn.hidden = true;
      // erst bei Seite 1 begonnen und alles da: „Alle X Artikel angezeigt“
      this.setStatus(this.offset === 0 ? fill(this.dataset.done, this.shown(), this.total) : fill(this.dataset.progress, this.shown(), this.total));
    }

    fail() {
      this.busy = false;
      this.fails = (this.fails || 0) + 1;
      // zweimal still neu versuchen, danach Knopf „Weitere Artikel laden“
      if (this.fails <= 2) {
        clearTimeout(this.retryT);
        this.retryT = setTimeout(() => this.load(), 1500 * this.fails);
        return;
      }
      this.classList.add('is-error');
      if (this.io) this.io.disconnect();
      this.setStatus(fill(this.dataset.progress, this.shown(), this.total));
      if (this.moreBtn) this.moreBtn.hidden = false;
    }

    async load() {
      if (this.busy || !this.next || !this.isConnected) return;
      this.busy = true;
      this.showLoading();
      let url;
      try {
        url = new URL(this.next, window.location.href);
        url.searchParams.set('section_id', this.dataset.section);
      } catch (e) {
        this.fail();
        return;
      }
      this.ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      let html;
      try {
        const res = await fetch(url.toString(), { credentials: 'same-origin', signal: this.ctrl ? this.ctrl.signal : undefined });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        html = await res.text();
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        this.fail();
        return;
      }
      if (!this.isConnected) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newList = this.findList(doc);
      const nextEl = doc.querySelector('pp-endless');
      // keine Section in der Antwort (z. B. Drosselung „local_rate_limited“ mit Status 200) = Fehler, nicht Ende
      if (!newList || !newList.children.length) {
        this.fail();
        return;
      }
      const frag = document.createDocumentFragment();
      if (newList) {
        Array.prototype.slice.call(newList.children).forEach((li) => {
          li.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
          li.removeAttribute('data-cascade');
          frag.appendChild(document.adoptNode(li));
        });
      }
      const added = frag.childNodes.length;
      this.list.appendChild(frag);
      this.next = nextEl ? nextEl.getAttribute('data-next') || '' : '';
      this.busy = false;
      this.fails = 0;

      if (!this.next || !added) {
        this.finish();
        return;
      }
      this.showLoading();
      // noch in Sichtweite? IntersectionObserver meldet sich erst bei Änderung – neu beobachten
      if (this.io) {
        this.io.unobserve(this);
        this.io.observe(this);
      }
      this.checkNear();
    }
  }

  customElements.define('pp-endless', PpEndless);
})();
